// components/Table/TableRowItem.jsx
/**
 * TableRowItem - Componente para exibição de uma linha de atendimento na tabela
 * 
 * Este componente renderiza uma linha individual da tabela de atendimentos, exibindo
 * as informações detalhadas de um único atendimento, incluindo seu ID, WhatsAppId,
 * ID de atendimento no CRM, data de cadastro e situação atual.
 */

// Importação dos componentes necessários do Material UI para construção da linha da tabela
import { TableCell, TableRow, Typography, Chip, Box } from '@mui/material';

// Importação da função utilitária para formatação de datas
import { formatDate } from '../../utils/date';

/**
 * Componente TableRowItem
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.atendimento - Objeto contendo os dados do atendimento a ser exibido
 * @param {number} props.atendimento.id - ID único do atendimento
 * @param {string} props.atendimento.whatsAppId - Identificador do WhatsApp
 * @param {string|number} props.atendimento.idAtendimentoCRM - ID do atendimento no sistema CRM
 * @param {string|Date} props.atendimento.dataHoraCadastroAtualizado - Data de cadastro ou atualização
 * @param {boolean} props.atendimento.concluido - Indica se o atendimento está concluído ou em aberto
 * @param {Function} props.onWhatsAppClick - Função de callback que será executada quando o WhatsAppId for clicado
 * @param {number} props.index - Índice da linha para alternância de cores
 * @returns {JSX.Element} Linha da tabela de atendimentos renderizada
 */
export default function TableRowItem({ atendimento, onWhatsAppClick, index }) {
  // Função para determinar a cor do chip baseado na situação
  const getSituacaoColor = (concluido) => {
    return concluido ? 'success' : 'warning';
  };

  return (
    // Linha da tabela com estilização moderna e efeitos hover
    <TableRow
      sx={{
        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
        '&:hover': {
          backgroundColor: '#e3f2fd',
          transform: 'scale(1.001)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        transition: 'all 0.2s ease-in-out',
        '& .MuiTableCell-root': {
          py: 2,
          fontSize: '0.875rem',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }
      }}
    >
      {/* Célula para o ID do atendimento com estilização moderna */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#495057',
          }}
        >
          #{atendimento.id}
        </Typography>
      </TableCell>
      
      {/* 
        Célula para o WhatsAppId
        Estilizada como um botão moderno e interativo
        Executa a função onWhatsAppClick passando o objeto de atendimento completo quando clicada
      */}
      <TableCell>
        <Box
          onClick={() => onWhatsAppClick(atendimento)}
          sx={{
            cursor: 'pointer',
            display: 'inline-block',
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: '#667eea',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontSize: '0.875rem',
            fontWeight: '500',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: 'rgba(102, 126, 234, 0.2)',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            },
          }}
        >
          {atendimento.whatsAppId}
        </Box>
      </TableCell>
      
      {/* Célula para o ID de atendimento no sistema CRM */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            color: '#495057',
            fontWeight: '500',
          }}
        >
          {atendimento.idAtendimentoCRM || 'N/A'}
        </Typography>
      </TableCell>
      
      {/* 
        Célula para a data de cadastro/atualização
        Utiliza a função formatDate para converter a data para o formato adequado de exibição
      */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            color: '#6c757d',
            fontSize: '0.8rem',
          }}
        >
          {formatDate(atendimento.dataHoraCadastroAtualizado)}
        </Typography>
      </TableCell>
      
      {/* 
        Célula para a situação do atendimento
        Exibe "Concluído" se a propriedade concluido for true, ou "Em Aberto" caso contrário
        Utiliza chip colorido para melhor visualização
      */}
      <TableCell>
        <Chip
          label={atendimento.concluido ? 'Concluído' : 'Em Aberto'}
          color={getSituacaoColor(atendimento.concluido)}
          size="small"
          onClick={() => {}}
          clickable={false}
          sx={{
            fontWeight: '500',
            fontSize: '0.75rem',
            borderRadius: 2,
            cursor: 'default',
          }}
        />
      </TableCell>
    </TableRow>
  );
}